import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { UsageChart } from '../components/UsageChart';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

interface UsageStats {
  today: {
    results: number;
    exports: number;
    enrichments: number;
    routeOptimizations: number;
    limit: number | null;
  };
  thisMonth: {
    results: number;
    exports: number;
    enrichments: number;
    routeOptimizations: number;
    limit: number | null;
  };
  features: {
    export: boolean;
    enrichment: boolean;
    routeOptimization: boolean;
  };
  subscriptionTier: string;
}

interface UsageHistory {
  daily: Array<{
    date: string;
    results: number;
    exports: number;
    enrichments: number;
    routeOptimizations: number;
  }>;
  totals: {
    results: number;
    exports: number;
    enrichments: number;
    routeOptimizations: number;
  };
  period: {
    days: number;
    start: string;
    end: string;
  };
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistory | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<7 | 30>(7);
  const [isLoading, setIsLoading] = useState(true);
  const [showCharts, setShowCharts] = useState(false);

  useEffect(() => {
    fetchUsage();
    fetchUsageHistory(historyPeriod);
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchUsageHistory(historyPeriod);
    }
  }, [historyPeriod, showCharts]);

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/usage`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data.usage);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsageHistory = async (days: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/usage/history?days=${days}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUsageHistory(data.history);
      }
    } catch (error) {
      console.error('Failed to fetch usage history:', error);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/portal`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'pro':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white';
      case 'starter':
        return 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white';
      case 'enterprise':
        return 'bg-slate-700 text-white';
      default:
        return 'bg-slate-700 text-slate-300';
    }
  };

  const getTierName = (tier: string) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="py-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">
                Welcome back, {user?.firstName}!
              </h1>
              <p className="mt-1 text-sm text-slate-400">
                Here's your account overview and usage statistics
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold text-center whitespace-nowrap flex-shrink-0 ${getTierColor(usage?.subscriptionTier || 'free')}`}>
              {getTierName(usage?.subscriptionTier || 'free')} Plan
            </span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/search"
              className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-6 text-white hover:from-blue-600 hover:to-cyan-600 transition-all transform hover:scale-105"
            >
              <div className="flex items-center">
                <svg className="h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <div>
                  <div className="text-sm font-medium opacity-90">Start Searching</div>
                  <div className="text-2xl font-bold">Find Businesses</div>
                </div>
              </div>
            </Link>

            <Link
              to="/pricing"
              className="bg-slate-800 rounded-lg p-6 border-2 border-slate-700 hover:border-blue-500 transition-colors"
            >
              <div className="flex items-center">
                <svg className="h-8 w-8 text-blue-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <div>
                  <div className="text-sm font-medium text-slate-400">Upgrade</div>
                  <div className="text-2xl font-bold text-slate-100">See Plans</div>
                </div>
              </div>
            </Link>

            {usage?.subscriptionTier !== 'free' && (
              <button
                onClick={handleManageSubscription}
                className="bg-slate-800 rounded-lg p-6 border-2 border-slate-700 hover:border-blue-500 transition-colors text-left"
              >
                <div className="flex items-center">
                  <svg className="h-8 w-8 text-blue-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-slate-400">Manage</div>
                    <div className="text-2xl font-bold text-slate-100">Subscription</div>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Usage Stats */}
        <div className="px-4 sm:px-0">
          <h2 className="text-xl font-bold text-slate-100 mb-4">Today's Usage</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Searches Today"
              value={usage?.today.results || 0}
              limit={usage?.today.limit}
              icon="🔍"
              color="blue"
            />
            <StatCard
              title="Exports"
              value={usage?.today.exports || 0}
              icon="📁"
              color="green"
              locked={!usage?.features.export}
            />
            <StatCard
              title="Enrichments"
              value={usage?.today.enrichments || 0}
              icon="✨"
              color="purple"
              locked={!usage?.features.enrichment}
            />
            <StatCard
              title="Route Optimizations"
              value={usage?.today.routeOptimizations || 0}
              icon="🗺️"
              color="cyan"
              locked={!usage?.features.routeOptimization}
            />
          </div>
        </div>

        {/* Monthly Stats */}
        {usage?.thisMonth.limit && (
          <div className="px-4 sm:px-0 mt-8">
            <h2 className="text-xl font-bold text-slate-100 mb-4">This Month's Usage</h2>
            <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-slate-400">Monthly Searches</p>
                  <p className="text-3xl font-bold text-slate-100">
                    {usage.thisMonth.results} / {usage.thisMonth.limit}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">
                    {Math.round(((usage.thisMonth.results / usage.thisMonth.limit) * 100))}% used
                  </p>
                </div>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((usage.thisMonth.results / usage.thisMonth.limit) * 100, 100)}%` }}
                ></div>
              </div>
              {usage.thisMonth.results > usage.thisMonth.limit * 0.8 && (
                <div className="mt-4 bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded">
                  <p className="text-sm text-yellow-300">
                    You're approaching your monthly limit. Consider upgrading for more searches.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Usage History Charts */}
        <div className="px-4 sm:px-0 mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-100">Usage History</h2>
            <button
              onClick={() => setShowCharts(!showCharts)}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center"
            >
              {showCharts ? 'Hide Charts' : 'Show Charts'}
              <svg
                className={`ml-2 h-4 w-4 transform transition-transform ${showCharts ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {showCharts && usageHistory && (
            <>
              {/* Period selector */}
              <div className="mb-6 flex space-x-2">
                <button
                  onClick={() => setHistoryPeriod(7)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    historyPeriod === 7
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => setHistoryPeriod(30)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    historyPeriod === 30
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  Last 30 Days
                </button>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <UsageChart
                  data={usageHistory.daily.map(d => ({ date: d.date, value: d.results }))}
                  title="Search Results"
                  color="#3b82f6"
                />
                {usage?.features.export && (
                  <UsageChart
                    data={usageHistory.daily.map(d => ({ date: d.date, value: d.exports }))}
                    title="Exports"
                    color="#10b981"
                  />
                )}
                {usage?.features.enrichment && (
                  <UsageChart
                    data={usageHistory.daily.map(d => ({ date: d.date, value: d.enrichments }))}
                    title="Enrichments"
                    color="#8b5cf6"
                  />
                )}
                {usage?.features.routeOptimization && (
                  <UsageChart
                    data={usageHistory.daily.map(d => ({ date: d.date, value: d.routeOptimizations }))}
                    title="Route Optimizations"
                    color="#06b6d4"
                  />
                )}
              </div>

              {/* Summary Stats */}
              <div className="mt-6 bg-slate-800 rounded-lg border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">
                  {historyPeriod}-Day Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Total Searches</p>
                    <p className="text-2xl font-bold text-slate-100">{usageHistory.totals.results}</p>
                  </div>
                  {usage?.features.export && (
                    <div>
                      <p className="text-sm text-slate-400">Total Exports</p>
                      <p className="text-2xl font-bold text-slate-100">{usageHistory.totals.exports}</p>
                    </div>
                  )}
                  {usage?.features.enrichment && (
                    <div>
                      <p className="text-sm text-slate-400">Total Enrichments</p>
                      <p className="text-2xl font-bold text-slate-100">{usageHistory.totals.enrichments}</p>
                    </div>
                  )}
                  {usage?.features.routeOptimization && (
                    <div>
                      <p className="text-sm text-slate-400">Route Optimizations</p>
                      <p className="text-2xl font-bold text-slate-100">{usageHistory.totals.routeOptimizations}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Upgrade CTA (for free users) */}
        {usage?.subscriptionTier === 'free' && (
          <div className="px-4 sm:px-0 mt-8">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg shadow-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">Unlock More Features</h3>
              <p className="text-blue-100 mb-6">
                Upgrade to get more searches, export data, optimize routes, and enrich business information.
              </p>
              <div className="flex space-x-4">
                <Link
                  to="/pricing"
                  className="bg-white text-blue-600 px-6 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({
  title,
  value,
  limit,
  icon,
  color,
  locked = false,
}: {
  title: string;
  value: number;
  limit?: number | null;
  icon: string;
  color: string;
  locked?: boolean;
}) {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    cyan: 'from-cyan-500 to-cyan-600',
  }[color];

  return (
    <div className="bg-slate-800 border border-slate-700 overflow-hidden shadow-lg rounded-lg relative">
      {locked && (
        <div className="absolute inset-0 bg-slate-900 bg-opacity-70 flex items-center justify-center z-10 rounded-lg backdrop-blur-sm">
          <div className="text-center">
            <svg className="h-8 w-8 text-slate-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <p className="text-xs text-slate-400 font-medium">Upgrade to unlock</p>
          </div>
        </div>
      )}
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 bg-gradient-to-br ${colorClasses} rounded-md p-3 text-2xl`}>
            {icon}
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-slate-400 truncate">{title}</dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-semibold text-slate-100">
                  {value}
                  {limit && <span className="text-sm text-slate-400 ml-1">/ {limit}</span>}
                </div>
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
